"use client";

import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { ChevronRight, Menu, X } from "lucide-react";
import { Link, usePathname } from "@/i18n/navigation";
import { Container } from "@/components/ui/Container";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { navRoutes, site, assets } from "@/lib/site";
import { ctaHref, loginElementId, trackCtaClick } from "@/lib/analytics";
import { cn } from "@/lib/utils";

// The scroll position has to be restored in the same frame the menu closes,
// before the browser paints the page behind it -- a passive effect would show
// one frame of a page scrolled back to the top. Falls back to useEffect during
// SSR, where neither hook runs anyway.
const useIsomorphicLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

export function Header() {
  const t = useTranslations("Nav");
  const pathname = usePathname();
  const isHome = pathname === "/";
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Scroll lock. `overflow: hidden` on <body> alone is not enough on iOS Safari
  // -- the page behind still scrolls with the touch. Pinning the body with
  // position: fixed at a negative top is the technique that holds there, so the
  // offset has to be restored by hand on close ("instant" because
  // scroll-behavior is smooth site-wide, and a 300ms glide back to where you
  // already were looks like a bug).
  useIsomorphicLayoutEffect(() => {
    if (!open) return;
    const { body } = document;
    const scrollY = window.scrollY;
    // Read from location, not from the `pathname` prop: this closure has to
    // compare against the URL as it is when the menu CLOSES, and the prop is
    // frozen at the value it had when the menu opened.
    const openedOn = window.location.pathname;
    const previous = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      overflow: body.style.overflow,
    };
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.overflow = "hidden";
    return () => {
      body.style.position = previous.position;
      body.style.top = previous.top;
      body.style.left = previous.left;
      body.style.right = previous.right;
      body.style.overflow = previous.overflow;
      // Only put the page back where it was if it is still the same page. A
      // back/forward navigation with the menu up closes it from the NEW page,
      // and restoring there would drag that page to the old one's offset.
      if (window.location.pathname === openedOn) {
        window.scrollTo({ top: scrollY, behavior: "instant" });
      }
    };
  }, [open]);

  // Keyboard handling while the menu is up: Escape closes it, and Tab cycles
  // within the panel plus its toggle. Without the cycle, tabbing past the last
  // link walks into the page behind -- content the panel is covering, so focus
  // would be somewhere the user cannot see. A layout effect, not a passive one,
  // so focus leaves the panel in the same commit that marks it inert.
  useIsomorphicLayoutEffect(() => {
    if (!open) return;

    const focusables = () => {
      const panel = panelRef.current;
      const toggle = toggleRef.current;
      if (!panel || !toggle) return [];
      // The toggle lives in the header, after the panel in the DOM, so it is
      // last in tab order -- which is where the cycle wraps.
      return [
        ...panel.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
        toggle,
      ];
    };

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // The language switcher renders a listbox inside the panel and closes
        // it on Escape too. One Escape should dismiss one layer: leave the
        // dropdown to itself and close the menu on the next press.
        // This runs in the CAPTURE phase for exactly this check -- the
        // switcher's own document listener is registered first, and by the time
        // a bubble-phase listener here ran, React had already flushed the
        // listbox out of the DOM and the guard saw nothing to defer to.
        if (panelRef.current?.querySelector('[role="listbox"]')) return;
        setOpen(false);
        return;
      }
      if (e.key !== "Tab") return;
      const items = focusables();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === panelRef.current)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKey, true);
    panelRef.current?.focus();
    // The toggle stays mounted while the menu is open (only its icon swaps), so
    // the node captured here is still the right one to hand focus back to.
    const toggle = toggleRef.current;
    return () => {
      document.removeEventListener("keydown", onKey, true);
      toggle?.focus();
    };
  }, [open]);

  // Browser back/forward moves the route without a click on one of the panel
  // links, which would otherwise leave the menu covering the new page. Adjusted
  // during render rather than in an effect: an effect would paint the new page
  // once with the menu still over it.
  const [routeAtRender, setRouteAtRender] = useState(pathname);
  if (routeAtRender !== pathname) {
    setRouteAtRender(pathname);
    setOpen(false);
  }

  // Close the mobile menu once the viewport reaches lg. The panel and its toggle
  // are lg:hidden, so without this `open` stays true with no way to unset it --
  // rotate a tablet or drag a window past 1024px with the menu open and the
  // header keeps the menu-open styling it can no longer show a way out of.
  // This used to be harmless because desktop forced a white bar regardless; the
  // hero is dark at every width now, so that fallback is gone.
  useEffect(() => {
    const desktop = window.matchMedia("(min-width: 1024px)");
    const closeIfDesktop = () => {
      if (desktop.matches) setOpen(false);
    };
    closeIfDesktop();
    desktop.addEventListener("change", closeIfDesktop);
    return () => desktop.removeEventListener("change", closeIfDesktop);
  }, []);

  // The homepage hero is full-bleed dark at every width, so the header sits on
  // it transparently until the user scrolls or opens the menu. Other pages have
  // no dark hero and keep the white header throughout.
  const darkBar = isHome && !scrolled && !open;

  return (
    <>
      {/* Mobile-homepage top edge for the iOS 26 toolbar color (see globals.css).
          A static white base strip with a solid ink strip stacked on top; the ink
          strip slides out of view on scroll via a scroll-driven animation, so the
          color under the toolbar is driven purely by a compositor change and
          re-samples in both scroll directions. The ink strip is what paints the
          VISIBLE dark header bar at the top; it unmounts while the menu is open so
          the bar reads white behind the open panel. The header itself stays
          bg-transparent on mobile home so it never becomes a JS-colored sample.
          Mobile + homepage only; header content paints above (z-50 vs z-30/z-20). */}
      {isHome && (
        <>
          <div aria-hidden className="toolbar-base lg:hidden" />
          {!open && <div aria-hidden className="toolbar-tint lg:hidden" />}
        </>
      )}

      {/* Full-screen mobile menu. It sits BELOW the header bar (z-40 vs z-50) so
          the wordmark and the close button stay visible and clickable on top of
          it, and it is always mounted so it can fade out as well as in -- see
          .mobile-menu in globals.css for why visibility, not display.

          Not role="dialog"/aria-modal: the control that opens and closes it is
          the header toggle, which is OUTSIDE the panel, and a modal that leaves
          its own close button outside itself is a promise to assistive tech
          that this markup cannot keep. It is a disclosure instead -- the toggle
          owns it through aria-expanded + aria-controls -- with the Tab cycle
          above doing the part that actually matters to a keyboard user.

          `inert` while closed is what stops the 200ms fade-out from swallowing
          taps and holding focus: opacity 0 alone leaves an element clickable,
          and under prefers-reduced-motion the fade collapses to nothing while
          the visibility delay does not. */}
      <div
        id="mobile-menu"
        ref={panelRef}
        tabIndex={-1}
        aria-hidden={!open}
        inert={!open}
        data-open={open || undefined}
        className="mobile-menu outline-none lg:hidden"
      >
        <div className="h-full overflow-y-auto overscroll-contain pt-16">
          <Container className="flex min-h-full flex-col pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-2">
            <nav className="flex flex-col">
              {navRoutes.map((route, i) => {
                const active = pathname === route.href;
                return (
                  <Link
                    key={route.key}
                    href={route.href}
                    onClick={() => setOpen(false)}
                    aria-current={active ? "page" : undefined}
                    style={{ "--i": i } as CSSProperties}
                    className={cn(
                      // Row size follows the viewport HEIGHT, not its width: all
                      // six rows plus the login button have to fit without the
                      // menu scrolling, and that is decided by height alone.
                      // Measured in WebKit at 568px (iPhone SE), 659px
                      // (iPhone 15) and 800px+.
                      "mobile-menu-item flex items-center justify-between border-b border-line py-3.5 text-xl font-semibold transition-colors active:text-brand",
                      "[@media(min-height:640px)]:py-4 [@media(min-height:760px)]:py-5 [@media(min-height:760px)]:text-2xl",
                      active ? "text-brand" : "text-navy",
                    )}
                  >
                    {t(route.key)}
                    <ChevronRight
                      aria-hidden
                      className={cn("size-5 shrink-0", active ? "text-brand" : "text-muted")}
                    />
                  </Link>
                );
              })}
            </nav>

            <div
              className="mobile-menu-item mt-auto pt-6 [@media(min-height:640px)]:pt-10"
              style={{ "--i": navRoutes.length } as CSSProperties}
            >
              <LanguageSwitcher align="left" className="mb-4 -ml-2 w-fit" />
              <a
                href={ctaHref(site.loginUrl, "header_login_mobile")}
                id={loginElementId("header_login_mobile")}
                onClick={() => trackCtaClick("header_login_mobile", site.loginUrl)}
                // Same button as the desktop header login: rounded-md, no
                // shadow. Only the width differs, because it is the one action
                // at the bottom of the panel.
                className="flex h-12 w-full items-center justify-center rounded-md bg-brand px-5 text-sm font-semibold text-white transition-colors active:bg-brand-dark"
              >
                {t("login")}
              </a>
            </div>
          </Container>
        </div>
      </div>

      <header
        className={cn(
          "fixed inset-x-0 top-0 z-50 transition-all duration-300",
          // On the mobile homepage the header is ALWAYS transparent so the
          // scroll-driven ink/white strips behind it are what iOS 26 samples for
          // the toolbar color (a JS-toggled header background does not re-sample
          // on scroll-up). On desktop home it is transparent over the dark hero
          // and turns frosted white once scrolled. Other pages have no dark hero,
          // so the header stays white throughout.
          isHome
            ? scrolled
              ? "bg-transparent lg:bg-white/90 lg:backdrop-blur-md"
              : "bg-transparent"
            : scrolled
              ? "bg-white lg:bg-white/90 lg:backdrop-blur-md"
              : "bg-white",
        )}
      >
        {/* Hairline separator as a child rather than border-b on the header: a
            border-bottom on a fixed element overrides the background Safari
            samples for the iOS 26 toolbar color. The open menu is one white
            surface from top to bottom, so the line goes while it is up. */}
        <div
          className={cn(
            "pointer-events-none absolute inset-x-0 bottom-0 h-px",
            darkBar || open ? "bg-transparent" : scrolled ? "bg-line/70" : "bg-line",
          )}
        />
        <Container className="flex h-16 items-center justify-between gap-4">
          <Link href="/" aria-label={site.name} className="shrink-0" onClick={() => setOpen(false)}>
            {/* White wordmark over the dark hero bar at the top, at every width;
                the navy wordmark everywhere else (scrolled, and all other pages). */}
            {/* unoptimized: these are SVGs, so /_next/image only proxies the bytes
                back unchanged -- in prod that is an optimizer Lambda round-trip for
                nothing. eager, not priority: React still emits a preload for a
                non-lazy image, but without priority that preload is Low, which is
                where the wordmark belongs relative to the hero photo. Do not add
                fetchPriority="low" to silence the preload -- it also pins the
                rendered <img> at Low, blocking the boost Chrome gives an
                in-viewport image after layout, and this wordmark is visible
                immediately. */}
            <Image
              src={assets.logoFooter}
              alt={site.name}
              width={172}
              height={38}
              unoptimized
              loading="eager"
              className={cn("h-7 w-auto", !darkBar && "hidden")}
            />
            <Image
              src={assets.logo}
              alt={site.name}
              width={172}
              height={38}
              unoptimized
              loading="eager"
              className={cn("h-7 w-auto", darkBar && "hidden")}
            />
          </Link>

          <nav className="hidden items-center gap-0.5 lg:flex">
            {navRoutes.map((route) => (
              <Link
                key={route.key}
                href={route.href}
                className={cn(
                  "rounded-full px-3 py-2 text-sm font-medium transition-colors",
                  darkBar
                    ? "text-white/85 hover:bg-white/10 hover:text-white"
                    : "text-slate hover:bg-mist hover:text-navy",
                )}
              >
                {t(route.key)}
              </Link>
            ))}
          </nav>

          <div className="hidden items-center gap-3 lg:flex">
            <LanguageSwitcher dark={darkBar} />
            <a
              href={ctaHref(site.loginUrl, "header_login")}
              id={loginElementId("header_login")}
              onClick={() => trackCtaClick("header_login", site.loginUrl)}
              className="rounded-md bg-brand px-5 py-2 text-sm font-semibold text-white transition hover:bg-brand-dark"
            >
              {t("login")}
            </a>
          </div>

          <button
            id="mobile-menu-toggle"
            ref={toggleRef}
            className={cn(
              "inline-flex size-10 items-center justify-center rounded-full transition-colors lg:hidden",
              darkBar ? "text-white" : "text-navy",
            )}
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? t("closeMenu") : t("openMenu")}
            aria-expanded={open}
            aria-controls="mobile-menu"
          >
            {open ? <X className="size-6" /> : <Menu className="size-6" />}
          </button>
        </Container>
      </header>
    </>
  );
}
